import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListActions from "@tCentro/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@tCentro/Overrides/Widgets/ProductList/ProductListProductImage";
import { COLORS } from "@tCentro/theme/design";
import CENProductListActions from "@tCentro/Widgets/Product/CENProductListActions";
import CENProductListPriceCart from "@tCentro/Widgets/Product/CENProductListPriceCart";
import CENProductListProductCompare from "@tCentro/Widgets/Product/CENProductListProductCompare";
import NISProductListProductInformationV2 from "@tCentro/Widgets/Product/NISProductListProductInformationV2";
import NISProductListActions from "@tCentro/Widgets/ProductList/NISProductListActions";
import NISProductListProductImageList from "@tCentro/Widgets/ProductList/NISProductListProductImageList";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    priceCartWrapper?: InjectableCss;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 0;
            min-width: 320px; /* prevent overlap with second column */
            display: flex;
            background: white !important;
            flex-direction: column;
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 14px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            div[class^="GridItemStyle"] {
                padding: 2px 5px !important;
            }
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            padding:0px !important;
            justify-content: flex-end;
               @media only screen and (min-device-width: 834px) and (max-device-width: 1357px) and (-webkit-min-device-pixel-ratio: 2) {
                          justify-content: flex-start;
                    }
                    @media (min-width: 1200px) {
                         justify-content: flex-end;
                    }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding: 4px 8px !important;
                        justify-content: flex-start;
                        
                    `,
                    null,
                    null,
                ])}
        `,
    },
    priceCartWrapper: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = productCardStyles;

const NISProductListProductCard: FC<Props> = props => {
    return (
        <StyledWrapper {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <StyledWrapper>
                <CENProductListProductCompare showCompare={props.showCompare} showAddToList={props.showAddToList} />
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <GridItem {...styles.leftColumnGridItem}>
                    {props.product.isSponsored && (
                        <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                    )}
                    <NISProductListProductImageList showImage={props.showImage} showCompare={props.showCompare} />
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <GridContainer {...styles.rightColumnGridContainer}>
                        <GridItem {...styles.infoGridItem}>
                            <NISProductListProductInformationV2
                                showBrand={props.showBrand}
                                showTitle={props.showTitle}
                                showPartNumbers={props.showPartNumbers}
                                showAvailability={props.showAvailability}
                                showAttributes={props.showAttributes}
                                showPrice={props.showPrice}
                            />
                        </GridItem>
                        <GridItem {...styles.actionsGridItem}>
                            <div>
                                <StyledWrapper>
                                    <NISProductListActions isListView showPrice={props.showPrice} />
                                </StyledWrapper>
                                <CENProductListActions />
                            </div>
                        </GridItem>
                        <GridItem {...styles.searchScoreDataGridItem}>
                            <ProductSearchScoreData />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(NISProductListProductCard);
